import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Flex, HStack, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { PiDevicesDuotone as FiMonitor, PiSignOutDuotone as FiLogOut } from "react-icons/pi";
import {
  ActiveSession,
  getActiveSessions,
  revokeSession,
  revokeOtherSessions
} from "@lubbezposrednio-frontend/core";
import { SecurityCard } from "./SecurityCard";

const ACCENT = "#2563EB";

const timeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "przed chwilą";
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} godz. temu`;
  return `${Math.floor(h / 24)} dni temu`;
};

export const ActiveSessionsCard: React.FC = () => {
  const toast = useToast();
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await getActiveSessions();
      setSessions(res.data);
    } catch {
      setSessions([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (id: string) => {
    setBusy(true);
    try {
      await revokeSession(id);
      setSessions(s => (s ? s.filter(x => x.id !== id) : s));
      toast({ title: "Sesja wylogowana.", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nie udało się wylogować sesji.", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const revokeOthers = async () => {
    setBusy(true);
    try {
      await revokeOtherSessions();
      setSessions(s => (s ? s.filter(x => x.current) : s));
      toast({ title: "Wylogowano wszystkie inne sesje.", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nie udało się wylogować innych sesji.", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const others = (sessions ?? []).filter(s => !s.current).length;

  return (
    <SecurityCard
      icon={FiMonitor}
      accent={ACCENT}
      full
      title="Aktywne sesje"
      subtitle="Urządzenia i przeglądarki, w których jesteś zalogowany. Nie poznajesz którejś? Wyloguj ją."
      action={
        others > 0 ? (
          <Button variant="link" color={ACCENT} fontWeight="700" fontSize="14px" leftIcon={<FiLogOut />} isDisabled={busy} onClick={revokeOthers}>
            Wyloguj wszystkie inne
          </Button>
        ) : undefined
      }
    >
      {sessions === null ? (
        <Spinner size="sm" color="primary" />
      ) : sessions.length === 0 ? (
        <Text fontSize="14px" color="gray.400">Brak aktywnych sesji do pokazania.</Text>
      ) : (
        <VStack align="stretch" spacing="10px">
          {sessions.map(s => (
            <Flex key={s.id} align="center" justify="space-between" p="12px 14px" borderRadius="12px" bg="rgba(37,99,235,0.04)" border="1px solid rgba(37,99,235,0.10)">
              <HStack spacing="12px" minW={0}>
                <Box color={ACCENT}><FiMonitor size={20} /></Box>
                <Box minW={0}>
                  <HStack spacing="8px">
                    <Text fontWeight="700" color="gray.700" noOfLines={1}>{s.device}</Text>
                    {s.current && <Badge colorScheme="green" borderRadius="6px">Ta sesja</Badge>}
                  </HStack>
                  <Text fontSize="12px" color="gray.400" noOfLines={1}>
                    {s.ip ? `${s.ip} · ` : ""}aktywna {timeAgo(s.last_used_at)}
                  </Text>
                </Box>
              </HStack>
              {!s.current && (
                <Button size="sm" variant="ghost" color="gray.500" fontWeight="700" isDisabled={busy} onClick={() => revoke(s.id)} _hover={{ color: "#E51F44", bg: "rgba(229,31,68,0.06)" }}>
                  Wyloguj
                </Button>
              )}
            </Flex>
          ))}
        </VStack>
      )}
    </SecurityCard>
  );
};
